import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import { ExternalLink, Youtube, AlertTriangle } from 'lucide-react';
import { SourceIconBadge } from '../SourceLogo/SourceLogo';
import { getCategoryIconKey, getCategoryLabel } from './filters/filterOptions';

const sortArrow = (sortConfig, key) => (
    sortConfig.key === key ? (sortConfig.direction === 'ascending' ? '↑' : '↓') : ''
);

const sortableClass = (sortConfig, key, extra = '') => (
    `sortable ${extra} ${sortConfig.key === key ? 'sorted' : ''}`.trim()
);

export function renderColumnHeader(key, ctx) {
    const { sortConfig, requestSort, currencySymbol } = ctx;

    if (key.startsWith('funnelStepPercentage_')) {
        const stepName = key.slice('funnelStepPercentage_'.length);
        return (
            <th
                key={key}
                className={sortableClass(sortConfig, key, 'percentage-column')}
                onClick={() => requestSort(key)}
            >
                {stepName} % from Clicks {sortArrow(sortConfig, key)}
            </th>
        );
    }

    if (key.startsWith('funnelStep_')) {
        const stepName = key.slice('funnelStep_'.length);
        return (
            <th
                key={key}
                className={sortableClass(sortConfig, key, 'funnel-step-column')}
                onClick={() => requestSort(key)}
            >
                {stepName} {sortArrow(sortConfig, key)}
            </th>
        );
    }

    switch (key) {
        case 'category':
            return <th key={key} className="category-column">Category</th>;
        case 'published_at':
            return (
                <th
                    key={key}
                    className={sortableClass(sortConfig, 'published_at')}
                    onClick={() => requestSort('published_at')}
                >
                    Published {sortArrow(sortConfig, 'published_at')}
                    <span className="tooltip-icon">?
                        <span className="tooltip-text">To update published dates, click the "Refresh YouTube Views" button.</span>
                    </span>
                </th>
            );
        case 'first_event':
            return (
                <th
                    key={key}
                    className={sortableClass(sortConfig, 'first_event')}
                    onClick={() => requestSort('first_event')}
                >
                    First Event {sortArrow(sortConfig, 'first_event')}
                    <span className="tooltip-icon">?
                        <span className="tooltip-text">Date of first click, sale, or call booking for this resource.</span>
                    </span>
                </th>
            );
        case 'source':
            return <th key={key}>Source</th>;
        case 'tags':
            return <th key={key}>Tags</th>;
        case 'views':
            return (
                <th
                    key={key}
                    className={sortableClass(sortConfig, 'views')}
                    onClick={() => requestSort('views')}
                >
                    Views {sortArrow(sortConfig, 'views')}
                </th>
            );
        case 'engagedViews':
            return (
                <th
                    key={key}
                    className={sortableClass(sortConfig, 'engaged_views', 'engaged-views-column')}
                    onClick={() => requestSort('engaged_views')}
                >
                    Engaged<br />Views {sortArrow(sortConfig, 'engaged_views')}
                </th>
            );
        case 'clicks':
            return (
                <th
                    key={key}
                    className={sortableClass(sortConfig, 'totalClicks')}
                    onClick={() => requestSort('totalClicks')}
                >
                    Clicks {sortArrow(sortConfig, 'totalClicks')}
                </th>
            );
        case 'callBookings':
            return (
                <th
                    key={key}
                    className={sortableClass(sortConfig, 'totalCallBookings')}
                    onClick={() => requestSort('totalCallBookings')}
                >
                    Call Bookings {sortArrow(sortConfig, 'totalCallBookings')}
                </th>
            );
        case 'emails':
            return (
                <th
                    key={key}
                    className={sortableClass(sortConfig, 'emails')}
                    onClick={() => requestSort('emails')}
                >
                    Emails{sortArrow(sortConfig, 'signups')}
                </th>
            );
        case 'sales':
            return (
                <th
                    key={key}
                    className={sortableClass(sortConfig, 'totalSales')}
                    onClick={() => requestSort('totalSales')}
                >
                    Sales{sortArrow(sortConfig, 'totalSales')}
                    <span className="tooltip-icon">?
                        <span className="tooltip-text">Conversions from offers with the type "paid offer". Change offer types in "Code and offers".</span>
                    </span>
                </th>
            );
        case 'revenue':
            return (
                <th
                    key={key}
                    className={sortableClass(sortConfig, 'totalRevenue', 'revenue-column')}
                    onClick={() => requestSort('totalRevenue')}
                >
                    ({currencySymbol}) {sortArrow(sortConfig, 'totalRevenue')}
                    <span className="tooltip-icon">?
                        <div className="tooltip-text">You can assign a conversion value to each paid offer in the "Code" section.</div>
                    </span>
                </th>
            );
        case 'clickPercentage':
            return (
                <th
                    key={key}
                    className={sortableClass(sortConfig, 'clickPercentage', 'percentage-column')}
                    onClick={() => requestSort('clickPercentage')}
                >
                    <div className="metric-label">
                        Clicks % from Views
                        <span className="tooltip-icon">?
                            <span className="tooltip-text">This figure is only accurate for videos published since you started using RevTrack</span>
                        </span>
                    </div>
                    {sortArrow(sortConfig, 'clickPercentage')}
                </th>
            );
        case 'callBookingsPercentage':
            return (
                <th
                    key={key}
                    className={sortableClass(sortConfig, 'callBookingsPercentage', 'percentage-column')}
                    onClick={() => requestSort('callBookingsPercentage')}
                >
                    Calls % from Clicks {sortArrow(sortConfig, 'callBookingsPercentage')}
                </th>
            );
        case 'salesPercentage':
            return (
                <th
                    key={key}
                    className={sortableClass(sortConfig, 'salesPercentage', 'percentage-column')}
                    onClick={() => requestSort('salesPercentage')}
                >
                    Sales % from Clicks {sortArrow(sortConfig, 'salesPercentage')}
                </th>
            );
        case 'emailsPercentage':
            return (
                <th
                    key={key}
                    className={sortableClass(sortConfig, 'emailsPercentage', 'percentage-column')}
                    onClick={() => requestSort('emailsPercentage')}
                >
                    Emails % from Clicks {sortArrow(sortConfig, 'emailsPercentage')}
                </th>
            );
        case 'conversionPercentageFromViews':
            return (
                <th
                    key={key}
                    className={sortableClass(sortConfig, 'conversionPercentageFromViews', 'percentage-column')}
                    onClick={() => requestSort('conversionPercentageFromViews')}
                >
                    Sales % from Views {sortArrow(sortConfig, 'conversionPercentageFromViews')}
                </th>
            );
        case 'callBookingConversionPercentage':
            return (
                <th
                    key={key}
                    className={sortableClass(sortConfig, 'callBookingConversionPercentage', 'percentage-column')}
                    onClick={() => requestSort('callBookingConversionPercentage')}
                >
                    Sales % from Calls {sortArrow(sortConfig, 'callBookingConversionPercentage')}
                </th>
            );
        case 'revenuePerView':
            return (
                <th
                    key={key}
                    className={sortableClass(sortConfig, 'revenuePerView', 'percentage-column')}
                    onClick={() => requestSort('revenuePerView')}
                >
                    {currencySymbol} per View {sortArrow(sortConfig, 'revenuePerView')}
                </th>
            );
        case 'emailsFromViewsPercentage':
            return (
                <th
                    key={key}
                    className={sortableClass(sortConfig, 'emailsFromViewsPercentage', 'percentage-column')}
                    onClick={() => requestSort('emailsFromViewsPercentage')}
                >
                    Emails % from Views {sortArrow(sortConfig, 'emailsFromViewsPercentage')}
                </th>
            );
        default:
            return null;
    }
}

export function renderColumnTotal(key, ctx) {
    const { calculateTotals, currencySymbol } = ctx;

    if (key.startsWith('funnelStepPercentage_')) {
        const stepName = key.slice('funnelStepPercentage_'.length);
        const value = calculateTotals.funnelStepPercentageTotals[stepName];
        return (
            <td key={key} className="percentage-column">
                <strong>{value}{value !== 'N/A' ? '%' : ''}</strong>
            </td>
        );
    }

    if (key.startsWith('funnelStep_')) {
        const stepName = key.slice('funnelStep_'.length);
        return (
            <td key={key} className="funnel-step-column">
                <strong>{new Intl.NumberFormat().format(calculateTotals.funnelStepTotals[stepName])}</strong>
            </td>
        );
    }

    switch (key) {
        case 'category':
            return <td key={key}><strong>TOTAL</strong></td>;
        case 'published_at':
        case 'first_event':
        case 'source':
        case 'tags':
            return <td key={key}></td>;
        case 'views':
            return <td key={key}><strong>{new Intl.NumberFormat().format(calculateTotals.views)}</strong></td>;
        case 'engagedViews':
            return <td key={key} className="engaged-views-column"><strong>{new Intl.NumberFormat().format(calculateTotals.engagedViews)}</strong></td>;
        case 'clicks':
            return <td key={key}><strong>{new Intl.NumberFormat().format(calculateTotals.totalClicks)}</strong></td>;
        case 'callBookings':
            return <td key={key}><strong>{new Intl.NumberFormat().format(calculateTotals.totalCallBookings)}</strong></td>;
        case 'emails':
            return <td key={key}><strong>{new Intl.NumberFormat().format(calculateTotals.totalSignups)}</strong></td>;
        case 'sales':
            return <td key={key}><strong>{new Intl.NumberFormat().format(calculateTotals.totalSales)}</strong></td>;
        case 'revenue':
            return <td key={key} className="revenue-column"><strong>{currencySymbol}{new Intl.NumberFormat().format(calculateTotals.totalRevenue.toFixed(2))}</strong></td>;
        case 'clickPercentage':
            return <td key={key} className="percentage-column"><strong>{calculateTotals.clickPercentage}%</strong></td>;
        case 'callBookingsPercentage':
            return <td key={key} className="percentage-column"><strong>{calculateTotals.callBookingsPercentage}%</strong></td>;
        case 'salesPercentage':
            return <td key={key} className="percentage-column"><strong>{calculateTotals.salesPercentage}%</strong></td>;
        case 'emailsPercentage':
            return <td key={key} className="percentage-column"><strong>{calculateTotals.signupsPercentage}%</strong></td>;
        case 'conversionPercentageFromViews':
            return <td key={key} className="percentage-column"><strong>{calculateTotals.conversionPercentageFromViews}%</strong></td>;
        case 'callBookingConversionPercentage':
            return <td key={key} className="percentage-column"><strong>{calculateTotals.callBookingConversionPercentage}%</strong></td>;
        case 'revenuePerView':
            return <td key={key} className="percentage-column"><strong>{calculateTotals.revenuePerView}</strong></td>;
        case 'emailsFromViewsPercentage':
            return (
                <td key={key} className="percentage-column">
                    <strong>{calculateTotals.views > 0 ? ((calculateTotals.totalSignups / calculateTotals.views) * 100).toFixed(2) : '0.00'}%</strong>
                </td>
            );
        default:
            return null;
    }
}

// Row expansion is handled by the <tr> in Dashboard.js; cells here only stop
// propagation where they contain their own interactive controls.
export function renderColumnCell(key, sale, index, ctx) {
    const {
        selectedOffer,
        currencySymbol,
        editingPublishedDate,
        tempPublishedDate,
        setTempPublishedDate,
        handleUpdatePublishedDate,
        cancelEditingPublishedDate,
        startEditingPublishedDate,
        isYoutubeResource,
        getYoutubeVideoId,
        showYtWarningTooltip,
        hideYtWarningTooltip,
        handleLinkResource,
        addingTagId,
        tagDraft,
        savingTagId,
        setTagDraft,
        setAddingTagId,
        handleAddTag,
        tagEditCancelled,
        clickPercentage,
        callBookingsPercentage,
        salesPercentage,
        signupsPercentage,
        formattedViews,
        formattedEngagedViews,
        formattedPublishedDate,
    } = ctx;

    if (key.startsWith('funnelStepPercentage_')) {
        const stepName = key.slice('funnelStepPercentage_'.length);
        const stepCount = sale.funnelStepCounts && sale.funnelStepCounts[stepName]
            ? Number(sale.funnelStepCounts[stepName]) : 0;
        return (
            <td key={key} className="percentage-column">
                {sale.totalClicks > 0
                    ? `${((stepCount / sale.totalClicks) * 100).toFixed(2)}%`
                    : 'N/A'}
            </td>
        );
    }

    if (key.startsWith('funnelStep_')) {
        const stepName = key.slice('funnelStep_'.length);
        let stepCount = 0;
        (sale.offers || []).forEach(offer => {
            if (selectedOffer === 'all' || offer.offer_name === selectedOffer) {
                const stepData = offer.funnel_steps?.find(step => step.step_name === stepName);
                if (stepData) {
                    stepCount += Number(stepData.conversion_count) || 0;
                }
            }
        });
        return (
            <td key={key} className="funnel-step-column">
                {stepCount}
            </td>
        );
    }

    switch (key) {
        case 'category':
            return (
                <td key={key} className="category-column">
                    <div className="category-cell__inner">
                        <span className="category-cell">
                            <SourceIconBadge
                                iconKey={getCategoryIconKey(sale.category)}
                                title={getCategoryLabel(sale.category)}
                            />
                            <span className="category-cell__label">{getCategoryLabel(sale.category)}</span>
                        </span>
                    </div>
                </td>
            );
        case 'published_at':
            return (
                <td key={key}>
                    {editingPublishedDate === sale.id ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                            <input
                                type="date"
                                value={tempPublishedDate}
                                onChange={(e) => setTempPublishedDate(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && tempPublishedDate) {
                                        e.stopPropagation();
                                        handleUpdatePublishedDate(sale.id, tempPublishedDate);
                                    } else if (e.key === 'Escape') {
                                        e.stopPropagation();
                                        cancelEditingPublishedDate();
                                    }
                                }}
                                style={{ fontSize: '12px', padding: '2px' }}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                            />
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleUpdatePublishedDate(sale.id, tempPublishedDate);
                                }}
                                style={{ fontSize: '10px', padding: '2px 4px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '2px' }}
                                disabled={!tempPublishedDate}
                            >
                                ✓
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    cancelEditingPublishedDate();
                                }}
                                style={{ fontSize: '10px', padding: '2px 4px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '2px' }}
                            >
                                ✕
                            </button>
                        </div>
                    ) : formattedPublishedDate ? (
                        <span>{formattedPublishedDate}</span>
                    ) : (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                startEditingPublishedDate(sale.id);
                            }}
                            className="set-date-button"
                        >
                            Click to Set
                        </button>
                    )}
                </td>
            );
        case 'first_event':
            return (
                <td key={key}>
                    {sale.first_event ? new Date(sale.first_event).toLocaleDateString() : 'N/A'}
                </td>
            );
        case 'source': {
            const displayText = isYoutubeResource(sale.category)
                ? sale.youtube_title || getYoutubeVideoId(sale) || sale.name
                : sale.name;
            return (
                <td key={key} className="name-column">
                    <div className="source-name-container">
                        <span
                            className={sale.link_url ? "clickable-link" : "source-text"}
                            title={displayText}
                            onClick={(e) => {
                                if (sale.link_url) {
                                    e.stopPropagation();
                                    window.open(sale.link_url, '_blank');
                                }
                            }}
                        >
                            {displayText}
                        </span>

                        {sale.link_url && (
                            <ExternalLink
                                size={14}
                                className="row-action-icon link-open-icon"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(sale.link_url, '_blank');
                                }}
                                title="Open link"
                            />
                        )}

                        {isYoutubeResource(sale.category) && sale.youtube_title && getYoutubeVideoId(sale) && (
                            <Youtube
                                size={16}
                                className="row-action-icon youtube-open-icon"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`https://www.youtube.com/watch?v=${getYoutubeVideoId(sale)}`, '_blank');
                                }}
                                title="Open YouTube video"
                            />
                        )}

                        {sale.category === 'qr' && !sale.youtube_video_id && (
                            <span
                                className="yt-warning-trigger qr-warning-trigger"
                                onClick={(e) => e.stopPropagation()}
                                onMouseEnter={(e) => showYtWarningTooltip(e, 'qr')}
                                onMouseLeave={hideYtWarningTooltip}
                            >
                                <AlertTriangle size={16} />
                            </span>
                        )}

                        {isYoutubeResource(sale.category)
                            && !(sale.category === 'qr' && !sale.youtube_video_id)
                            && !sale.youtube_title && (
                            <span
                                className="yt-warning-trigger"
                                onClick={(e) => e.stopPropagation()}
                                onMouseEnter={showYtWarningTooltip}
                                onMouseLeave={hideYtWarningTooltip}
                            >
                                <AlertTriangle size={16} />
                            </span>
                        )}

                        {!isYoutubeResource(sale.category) && !sale.link_url && (
                            <FontAwesomeIcon
                                icon={faLink}
                                className="link-icon"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleLinkResource(sale);
                                }}
                                title="Add URL link"
                            />
                        )}
                    </div>
                </td>
            );
        }
        case 'tags':
            return (
                <td key={key}>
                    <div className="tags-display table-tags">
                        {sale.link_tags && sale.link_tags.length > 0 && (
                            <div
                                className="tags-scroller"
                                title={sale.link_tags.map(t => t.trim()).filter(Boolean).join(', ')}
                            >
                                {sale.link_tags.map((tag, tagIndex) => (
                                    <span key={tagIndex} className="tag-box">
                                        {tag.trim()}
                                    </span>
                                ))}
                            </div>
                        )}
                        {addingTagId === sale.id ? (
                            <input
                                className="tag-add-input"
                                value={tagDraft}
                                autoFocus
                                disabled={savingTagId === sale.id}
                                placeholder="Tag"
                                aria-label="New tag"
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setTagDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ',') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleAddTag(sale, tagDraft);
                                    } else if (e.key === 'Escape') {
                                        e.stopPropagation();
                                        tagEditCancelled.current = true;
                                        setAddingTagId(null);
                                        setTagDraft('');
                                    }
                                }}
                                onBlur={() => {
                                    if (tagEditCancelled.current) {
                                        tagEditCancelled.current = false;
                                        return;
                                    }
                                    if (savingTagId === sale.id) return;
                                    if (tagDraft.trim()) {
                                        handleAddTag(sale, tagDraft);
                                    } else {
                                        setAddingTagId(null);
                                    }
                                }}
                            />
                        ) : (
                            <button
                                type="button"
                                className="tag-add-button"
                                title="Add tag"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    tagEditCancelled.current = false;
                                    setAddingTagId(sale.id);
                                    setTagDraft('');
                                }}
                            >
                                + add
                            </button>
                        )}
                    </div>
                </td>
            );
        case 'views':
            return <td key={key}>{formattedViews}</td>;
        case 'engagedViews':
            return <td key={key} className="engaged-views-column">{formattedEngagedViews}</td>;
        case 'clicks':
            return <td key={key}>{Number(sale.totalClicks)}</td>;
        case 'callBookings':
            return <td key={key}>{Number(sale.totalCallBookings)}</td>;
        case 'emails':
            return <td key={key}>{Number(sale.totalSignups)}</td>;
        case 'sales':
            return <td key={key}>{Number(sale.totalSales)}</td>;
        case 'revenue':
            return <td key={key} className="revenue-column">{currencySymbol}{Number(sale.totalRevenue).toFixed(2)}</td>;
        case 'clickPercentage':
            return <td key={key} className="percentage-column">{clickPercentage}%</td>;
        case 'callBookingsPercentage':
            return <td key={key} className="percentage-column">{callBookingsPercentage}%</td>;
        case 'salesPercentage':
            return <td key={key} className="percentage-column">{salesPercentage}%</td>;
        case 'emailsPercentage':
            return <td key={key} className="percentage-column">{signupsPercentage}%</td>;
        case 'conversionPercentageFromViews':
            return (
                <td key={key} className="percentage-column">
                    {sale.views ? ((sale.totalSales / sale.views) * 100).toFixed(2) : 'N/A'}%
                </td>
            );
        case 'callBookingConversionPercentage':
            return (
                <td key={key} className="percentage-column">
                    {sale.totalCallBookings ? ((sale.totalSales / sale.totalCallBookings) * 100).toFixed(2) : 'N/A'}%
                </td>
            );
        case 'revenuePerView':
            return (
                <td key={key} className="percentage-column">
                    {sale.views ? (sale.totalRevenue / sale.views).toFixed(4) : 'N/A'}
                </td>
            );
        case 'emailsFromViewsPercentage':
            return (
                <td key={key} className="percentage-column">
                    {sale.views > 0 ? ((Number(sale.totalSignups) / Number(sale.views)) * 100).toFixed(2) : '0.00'}%
                </td>
            );
        default:
            return null;
    }
}

export function columnCsvHeader(key, currencySymbol) {
    if (key.startsWith('funnelStepPercentage_')) {
        return `${key.slice('funnelStepPercentage_'.length)} % from Clicks`;
    }
    if (key.startsWith('funnelStep_')) {
        return key.slice('funnelStep_'.length);
    }
    const headers = {
        category: 'Category',
        published_at: 'Published Date',
        first_event: 'First Event',
        source: 'Source',
        tags: 'Tags',
        views: 'Views',
        engagedViews: 'Engaged Views',
        clicks: 'Clicks',
        callBookings: 'Call Bookings',
        emails: 'Emails',
        sales: 'Sales',
        revenue: 'Revenue',
        clickPercentage: 'Clicks % from Views',
        callBookingsPercentage: 'Calls % from Clicks',
        salesPercentage: 'Sales % from Clicks',
        emailsPercentage: 'Emails % from Clicks',
        conversionPercentageFromViews: 'Sales % from Views',
        callBookingConversionPercentage: 'Sales % from Calls',
        revenuePerView: `${currencySymbol} per View`,
        emailsFromViewsPercentage: 'Emails % from Views',
    };
    return headers[key] || null;
}

export function columnCsvValue(key, sale, ctx) {
    const { escapeCSVField, isYoutubeResource, getYoutubeVideoId } = ctx;

    if (key.startsWith('funnelStepPercentage_')) {
        const stepName = key.slice('funnelStepPercentage_'.length);
        const stepCount = sale.funnelStepCounts && sale.funnelStepCounts[stepName]
            ? Number(sale.funnelStepCounts[stepName]) : 0;
        return sale.totalClicks > 0 ? ((stepCount / sale.totalClicks) * 100).toFixed(2) : 'N/A';
    }

    if (key.startsWith('funnelStep_')) {
        const stepName = key.slice('funnelStep_'.length);
        const stepCount = sale.funnelStepCounts && sale.funnelStepCounts[stepName]
            ? Number(sale.funnelStepCounts[stepName]) : 0;
        return stepCount;
    }

    switch (key) {
        case 'category':
            return escapeCSVField(sale.category);
        case 'published_at':
            return escapeCSVField(sale.published_at ? new Date(sale.published_at).toLocaleDateString() : 'N/A');
        case 'first_event':
            return escapeCSVField(sale.first_event ? new Date(sale.first_event).toLocaleDateString() : 'N/A');
        case 'source': {
            const sourceName = isYoutubeResource(sale.category)
                ? sale.youtube_title || getYoutubeVideoId(sale) || sale.name
                : sale.name;
            return escapeCSVField(sourceName);
        }
        case 'tags':
            return escapeCSVField(sale.link_tags || '');
        case 'views':
            return sale.views ? sale.views : 'N/A';
        case 'engagedViews':
            return sale.engaged_views ? sale.engaged_views : 'N/A';
        case 'clicks':
            return Number(sale.totalClicks);
        case 'callBookings':
            return Number(sale.totalCallBookings);
        case 'emails':
            return Number(sale.totalSignups);
        case 'sales':
            return Number(sale.totalSales);
        case 'revenue':
            return Number(sale.totalRevenue).toFixed(2);
        case 'clickPercentage':
            return sale.views ? ((sale.totalClicks / sale.views) * 100).toFixed(2) : 'N/A';
        case 'callBookingsPercentage':
            return sale.totalClicks ? ((sale.totalCallBookings / sale.totalClicks) * 100).toFixed(2) : 'N/A';
        case 'salesPercentage':
            return sale.totalClicks ? ((sale.totalSales / sale.totalClicks) * 100).toFixed(2) : 'N/A';
        case 'emailsPercentage':
            return sale.totalClicks ? ((sale.totalSignups / sale.totalClicks) * 100).toFixed(2) : 'N/A';
        case 'conversionPercentageFromViews':
            return sale.views ? ((sale.totalSales / sale.views) * 100).toFixed(2) : 'N/A';
        case 'callBookingConversionPercentage':
            return sale.totalCallBookings ? ((sale.totalSales / sale.totalCallBookings) * 100).toFixed(2) : 'N/A';
        case 'revenuePerView':
            return sale.views ? (sale.totalRevenue / sale.views).toFixed(4) : 'N/A';
        case 'emailsFromViewsPercentage': {
            const emailsFromViewsPercentage = sale.views > 0
                ? ((Number(sale.totalSignups) / Number(sale.views)) * 100).toFixed(2)
                : '0.00';
            return `${emailsFromViewsPercentage}%`;
        }
        default:
            return null;
    }
}
